package com.karyadina.model

import kotlinx.serialization.Serializable

/**
 * Image resize configuration
 */
@Serializable
data class ImageResizeConfig(
    val targetSizeKB: Int? = null, // Target file size in KB (e.g., 20KB, 50KB)
    val targetWidth: Int? = null, // Target width in pixels
    val targetHeight: Int? = null, // Target height in pixels
    val quality: Int = 85, // JPEG quality 0-100
    val maintainAspectRatio: Boolean = true,
    val format: ImageFormat = ImageFormat.JPEG,
    val cropRect: CropRect? = null // Normalized crop rect (0.0-1.0)
)

@Serializable
data class CropRect(
    val left: Float,
    val top: Float,
    val right: Float,
    val bottom: Float
)

/**
 * Supported image formats
 */
enum class ImageFormat(val mimeType: String, val extension: String) {
    JPEG("image/jpeg", ".jpg"),
    PNG("image/png", ".png"),
    WEBP("image/webp", ".webp")
}

/**
 * Image resize preset for common use cases
 */
enum class ResizePreset(
    val displayName: String,
    val description: String,
    val targetSizeKB: Int? = null,
    val targetWidth: Int? = null,
    val targetHeight: Int? = null
) {
    SIGNATURE("Signature (20KB)", "For form signatures", targetSizeKB = 20, targetWidth = 400),
    PASSPORT("Passport Photo (50KB)", "For passport-sized photos", targetSizeKB = 50, targetWidth = 600),
    PROFILE("Profile Picture (100KB)", "For social media profiles", targetSizeKB = 100, targetWidth = 800),
    THUMBNAIL("Thumbnail (30KB)", "Small preview images", targetSizeKB = 30, targetWidth = 300),
    DOCUMENT("Document Scan (200KB)", "For document attachments", targetSizeKB = 200, targetWidth = 1200),
    CUSTOM("Custom", "Custom settings", null, null, null)
}

/**
 * Result of image resize operation
 */
data class ImageResizeResult(
    val success: Boolean,
    val originalImage: ImageItem,
    val resizedUri: String? = null,
    val resizedSizeInBytes: Long = 0,
    val resizedWidth: Int = 0,
    val resizedHeight: Int = 0,
    val compressionRatio: Double = 0.0,
    val error: String? = null
) {
    val resizedSizeInKB: Double
        get() = resizedSizeInBytes / 1024.0
    
    val savedPercentage: Double
        get() = if (originalImage.sizeInBytes > 0) {
            ((originalImage.sizeInBytes - resizedSizeInBytes).toDouble() / originalImage.sizeInBytes) * 100
        } else 0.0
}
